import { useRef, useState, useMemo } from 'react'
import { useBrands, useProducts, useSubscriptions } from '../../hooks/useApi'
import {
  useSaveBanner,
  useGenerateAiBannerDesign,
} from '../../hooks/useMarketing'
import { Button } from '../../components/ui/Button'
import { Card, CardContent } from '../../components/ui/Card'
import { BannerCanvas } from '../../components/marketing/BannerCanvas'
import { ShareCenter } from '../../components/marketing/ShareCenter'
import {
  BANNER_TEMPLATES,
  BANNER_SIZES,
  PROMO_TYPES,
  AI_BANNER_STYLES,
  type BannerSizeId,
} from '../../lib/marketing/constants'
import { buildBannerData, generateQrDataUrl } from '../../lib/marketing/utils'
import { exportElementAsImage, downloadDataUrl } from '../../lib/marketing/share'
import { normalizePlanId } from '../../lib/plans'
import { Sparkles, Wand2 } from 'lucide-react'
import type { AiBannerDesign } from '../../lib/marketing/types'
import { Link } from 'react-router-dom'

export default function MarketingBanner() {
  const { data: brands } = useBrands()
  const { data: products } = useProducts()
  const { data: subscriptions } = useSubscriptions()
  const plan = normalizePlanId(subscriptions?.[0]?.plan)
  const isPremium = plan === 'premium'

  const [brandId, setBrandId] = useState<number | ''>('')
  const [productId, setProductId] = useState<number | ''>('')
  const [promoType, setPromoType] = useState('flash_sale')
  const [templateId, setTemplateId] = useState(BANNER_TEMPLATES[0].id)
  const [sizeId, setSizeId] = useState<BannerSizeId>('instagram_feed')
  const [useAi, setUseAi] = useState(false)
  const [aiStyle, setAiStyle] = useState('modern')
  const [aiDesign, setAiDesign] = useState<AiBannerDesign | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [qrDataUrl, setQrDataUrl] = useState<string | undefined>()
  const [generating, setGenerating] = useState(false)

  const canvasRef = useRef<HTMLDivElement>(null)
  const saveBanner = useSaveBanner()
  const generateAiDesign = useGenerateAiBannerDesign()

  const brand = brands?.find((b: { id: number }) => b.id === brandId)
  const brandProducts = products?.filter((p: { brandId: number }) => p.brandId === brandId) ?? []
  const product = brandProducts.find((p: { id: number }) => p.id === productId)
  const template = BANNER_TEMPLATES.find((t) => t.id === templateId) || BANNER_TEMPLATES[0]

  const bannerData = useMemo(() => {
    if (!brand || !product) return null
    return buildBannerData({ brand, product, promoType, plan, qrDataUrl })
  }, [brand, product, promoType, plan, qrDataUrl])

  const handleGenerate = async () => {
    if (!brand || !product || !bannerData) return
    setGenerating(true)
    try {
      let design = aiDesign
      if (useAi && isPremium) {
        const result = await generateAiDesign.mutateAsync({
          brandId: brand.id,
          productId: product.id,
          promoType,
          style: aiStyle,
        })
        design = result.design
        setAiDesign(design)
      }

      const qr = await generateQrDataUrl(bannerData.catalogUrl)
      setQrDataUrl(qr)

      await new Promise((r) => setTimeout(r, 200))
      const el = canvasRef.current
      if (!el) throw new Error('Canvas tidak tersedia')

      const size = BANNER_SIZES[sizeId]
      const dataUrl = await exportElementAsImage(el, 'png', size.width, size.height)
      setPreviewUrl(dataUrl)
    } catch (e) {
      console.error(e)
    } finally {
      setGenerating(false)
    }
  }

  const handleSave = async () => {
    if (!brand || !product || !previewUrl) return
    await saveBanner.mutateAsync({
      brandId: brand.id,
      productId: product.id,
      promoType,
      templateId: useAi ? `ai-${aiStyle}` : templateId,
      title: `${product.name} - ${PROMO_TYPES.find((p) => p.id === promoType)?.label}`,
      isAiGenerated: useAi,
      aiStyle: useAi ? aiStyle : null,
      imageData: { [sizeId]: previewUrl },
      metadata: { sizeId, layout: template.layout },
    })
  }

  const handleDownload = (format: 'png' | 'jpg') => {
    if (!previewUrl) return
    downloadDataUrl(previewUrl, `banner-${product?.name || 'promo'}.${format}`)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Banner Promosi</h1>
        <p className="text-gray-600 mt-1">Buat banner profesional dari template atau AI Banner (Premium)</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
              <select
                className="w-full border rounded-lg px-3 py-2"
                value={brandId}
                onChange={(e) => { setBrandId(Number(e.target.value) || ''); setProductId('') }}
              >
                <option value="">Pilih Brand</option>
                {brands?.map((b: { id: number; name: string }) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Produk</label>
              <select
                className="w-full border rounded-lg px-3 py-2"
                value={productId}
                onChange={(e) => setProductId(Number(e.target.value) || '')}
                disabled={!brandId}
              >
                <option value="">Pilih Produk</option>
                {brandProducts.map((p: { id: number; name: string }) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Jenis Promosi</label>
              <select className="w-full border rounded-lg px-3 py-2" value={promoType} onChange={(e) => setPromoType(e.target.value)}>
                {PROMO_TYPES.map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
            </div>

            {!useAi && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Template ({BANNER_TEMPLATES.length} tersedia)</label>
                  <select className="w-full border rounded-lg px-3 py-2" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                    {BANNER_TEMPLATES.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ukuran</label>
              <select className="w-full border rounded-lg px-3 py-2" value={sizeId} onChange={(e) => setSizeId(e.target.value as BannerSizeId)}>
                {Object.entries(BANNER_SIZES).map(([id, s]) => (
                  <option key={id} value={id}>{s.label} ({s.width}x{s.height})</option>
                ))}
              </select>
            </div>

            {isPremium ? (
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={useAi} onChange={(e) => setUseAi(e.target.checked)} />
                  <Sparkles className="w-4 h-4 text-purple-600" />
                  <span className="font-medium text-purple-900">AI Banner (Premium) — 1 AI Boost</span>
                </label>
                {useAi && (
                  <select className="w-full border rounded-lg px-3 py-2 mt-3" value={aiStyle} onChange={(e) => setAiStyle(e.target.value)}>
                    {AI_BANNER_STYLES.map((s) => (
                      <option key={s.id} value={s.id}>{s.label}</option>
                    ))}
                  </select>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                <Link to="/subscription" className="text-indigo-600 hover:underline">Upgrade Premium</Link> untuk AI Banner
              </p>
            )}

            <Button
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={handleGenerate}
              disabled={!brand || !product || generating}
            >
              <Wand2 className="w-4 h-4 mr-2" />
              {generating ? 'Membuat Banner...' : 'Generate'}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <h3 className="font-semibold mb-4">Preview</h3>
            {previewUrl ? (
              <div className="space-y-4">
                <img src={previewUrl} alt="Preview banner" className="w-full rounded-lg border shadow-sm" />
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleDownload('png')}>Download PNG</Button>
                  <Button variant="outline" size="sm" onClick={() => handleDownload('jpg')}>Download JPG</Button>
                  <Button size="sm" onClick={handleSave} disabled={saveBanner.isPending}>Simpan</Button>
                </div>
                <ShareCenter
                  imageDataUrl={previewUrl}
                  filename={`banner-${product?.name}.png`}
                  caption={`${product?.name} - ${brand?.name}`}
                />
              </div>
            ) : (
              <div className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center text-gray-400">
                Pilih brand & produk, lalu klik Generate
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {bannerData && (
        <BannerCanvas
          ref={canvasRef}
          layout={template.layout}
          variant={template.variant}
          sizeId={sizeId}
          data={bannerData}
          aiDesign={useAi ? aiDesign ?? undefined : undefined}
        />
      )}
    </div>
  )
}
